<script lang="ts" setup>
    import { ref, onMounted, onBeforeUnmount, computed } from "vue"

    // Interface pour l'événement beforeinstallprompt
    interface BeforeInstallPromptEvent extends Event {
        prompt: () => Promise<void>
        userChoice: Promise<{ outcome: "accepted" | "dismissed" }>
    }

    // Stockage de l'événement d'installation
    const deferredPrompt = ref<BeforeInstallPromptEvent | null>(null)
    const showPrompt = ref(false)
    const dismissed = ref(false)

    // Clé localStorage pour ne pas réafficher si l'utilisateur a fermé
    const STORAGE_KEY = "pwa-install-prompt-dismissed"
    const DISMISS_DURATION_DAYS = 7

    // Vérifier si on est sur iOS (Safari)
    const isIOS = computed(() => {
        return (
            /iPad|iPhone|iPod/.test(navigator.userAgent) &&
            !(window as any).MSStream
        )
    })

    // Vérifier si l'app est déjà installée
    const isStandalone = computed(() => {
        return (
            window.matchMedia("(display-mode: standalone)").matches ||
            (window.navigator as any).standalone === true
        )
    })

    // Handler pour l'événement beforeinstallprompt
    function handleBeforeInstallPrompt(e: Event) {
        // Empêcher Chrome d'afficher son propre prompt
        e.preventDefault()
        // Stocker l'événement pour l'utiliser plus tard
        deferredPrompt.value = e as BeforeInstallPromptEvent

        // Vérifier si l'utilisateur n'a pas déjà fermé le prompt récemment
        const dismissedAt = localStorage.getItem(STORAGE_KEY)
        if (dismissedAt) {
            const dismissedDate = new Date(dismissedAt)
            const now = new Date()
            const diffDays =
                (now.getTime() - dismissedDate.getTime()) /
                (1000 * 60 * 60 * 24)
            if (diffDays < DISMISS_DURATION_DAYS) {
                return
            }
        }

        // Afficher le prompt après un court délai
        setTimeout(() => {
            showPrompt.value = true
        }, 3000)
    }

    // Handler quand l'app est installée
    function handleAppInstalled() {
        deferredPrompt.value = null
        showPrompt.value = false
        localStorage.removeItem(STORAGE_KEY)
    }

    // Installer l'application
    async function install() {
        if (!deferredPrompt.value) return

        // Afficher le prompt d'installation natif
        await deferredPrompt.value.prompt()

        // Attendre la réponse de l'utilisateur
        const { outcome } = await deferredPrompt.value.userChoice

        if (outcome === "accepted") {
            showPrompt.value = false
        }

        // L'événement ne peut être utilisé qu'une fois
        deferredPrompt.value = null
    }

    // Fermer le prompt et mémoriser
    function close() {
        showPrompt.value = false
        dismissed.value = true
        localStorage.setItem(STORAGE_KEY, new Date().toISOString())
    }

    onMounted(() => {
        // Ne pas afficher si déjà installé ou sur iOS
        if (isStandalone.value || isIOS.value) {
            return
        }

        window.addEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt,
        )
        window.addEventListener("appinstalled", handleAppInstalled)
    })

    onBeforeUnmount(() => {
        window.removeEventListener(
            "beforeinstallprompt",
            handleBeforeInstallPrompt,
        )
        window.removeEventListener("appinstalled", handleAppInstalled)
    })
</script>

<template>
    <v-snackbar
        v-model="showPrompt"
        color="primary"
        :timeout="-1"
        location="bottom right"
    >
        <div class="d-flex align-center">
            <v-icon class="mr-3">mdi-download</v-icon>
            <span>Installer l'application pour un accès rapide</span>
        </div>

        <template v-slot:actions>
            <v-btn variant="text" @click="install"> Installer </v-btn>
            <v-btn variant="text" @click="close"> Plus tard </v-btn>
        </template>
    </v-snackbar>
</template>
